// b15: katalogen som följer med koden. Datan ligger som filer i data/, och utgåvans nummer i
// data/utgava.json. Molnet läser in den i schemat katalog, och navet har den som reserv när det
// inte har någon giltig utgåva från molnet (BK39 §3).

import enhet from "../data/enhet.json" with { type: "json" };
import formaga from "../data/formaga.json" with { type: "json" };
import fraga from "../data/fraga.json" with { type: "json" };
import koppling from "../data/koppling.json" with { type: "json" };
import mall from "../data/mall.json" with { type: "json" };
import nmea2000 from "../data/nmea2000.json" with { type: "json" };
import signal from "../data/signal.json" with { type: "json" };
import system from "../data/system.json" with { type: "json" };
import utgava from "../data/utgava.json" with { type: "json" };
import {
  BLUETOOTHSORTER,
  FORMAGOR,
  FYNDKALLOR,
  HAKLASSER,
  type Katalog,
  type Katalogdata,
  LARMKALLOR,
  PLATSTYPER,
  PRODUKTER,
  STATUSAR,
  VAGAR,
  VICTRONTJANSTER,
} from "./typer.ts";

/** Katalogen i koden: navets reserv, och det som molnet läser in. */
export const KATALOGEN: Katalog = {
  nummer: utgava.nummer,
  system,
  enhet,
  signal,
  koppling,
  mall,
  fraga,
  nmea2000,
  formaga,
} as Katalog;

/** Utgåvans nummer och datans hash, som provet jämför med (en ändrad data kräver ett nytt nummer). */
export const UTGAVAN = utgava;

const ID = /^[a-z0-9][a-z0-9_.-]{0,63}$/;
const NAMN = /^[^\p{Cc}]{1,120}$/u;
const TEXT = /^[^\p{Cc}]{1,400}$/u;

function regex(s: unknown): boolean {
  if (typeof s !== "string" || s.length > 200) return false;
  try {
    new RegExp(s, "i");
    return true;
  } catch {
    return false;
  }
}

function produkttext(t: unknown): boolean {
  if (typeof t === "string") return t.length > 0 && t.length <= 600;
  if (typeof t !== "object" || t === null || Array.isArray(t)) return false;
  const v = Object.entries(t);
  return (
    v.length > 0 &&
    v.every(
      ([p, s]) =>
        (PRODUKTER as readonly string[]).includes(p) &&
        typeof s === "string" &&
        s.length > 0 &&
        s.length <= 600,
    )
  );
}

const ena = (v: unknown, lista: readonly string[]) => lista.includes(v as string);

/** Kontrollerar katalogens data. Returnerar det första felet, eller null. */
export function kontrolleraKatalog(k: Katalog): string | null {
  if (typeof k !== "object" || k === null) return "katalogen saknas";
  if (!Number.isSafeInteger(k.nummer) || k.nummer < 1) return "utgåvans nummer";
  const d = k as Katalogdata;
  for (const n of ["system", "enhet", "signal", "koppling", "mall", "fraga", "formaga"] as const) {
    if (!Array.isArray(d[n])) return `${n} saknas`;
  }
  const system = new Set<string>();
  for (const s of d.system) {
    if (!ID.test(s.id) || system.has(s.id)) return `systemet ${s.id}`;
    system.add(s.id);
    if (!NAMN.test(s.namn)) return `systemets namn: ${s.id}`;
    if (!ena(s.anslutning, ["buss", "lokalt", "bluetooth", "moln"])) return `anslutningen: ${s.id}`;
    if (s.agaren !== null && !TEXT.test(s.agaren)) return `ägarens del: ${s.id}`;
    if (!ena(s.styrning, ["nej", "granskad"])) return `styrningen: ${s.id}`;
    if (!ena(s.status, STATUSAR) || !ena(s.vag, VAGAR)) return `status och väg: ${s.id}`;
    if (s.kalla !== null && !ena(s.kalla, FYNDKALLOR)) return `källan: ${s.id}`;
    if (s.status === "planerad" && s.kalla !== null)
      return `ett planerat system har ingen kod: ${s.id}`;
  }
  const enheter = new Set<string>();
  for (const e of d.enhet) {
    if (!ID.test(e.id) || enheter.has(e.id)) return `enheten ${e.id}`;
    enheter.add(e.id);
    if (!system.has(e.system)) return `enhetens system: ${e.id}`;
    for (const t of [e.tillverkare, e.modell, e.namn])
      if (!NAMN.test(t)) return `enhetens namn: ${e.id}`;
    if (!ena(e.status, STATUSAR) || !ena(e.vag, VAGAR)) return `status och väg: ${e.id}`;
    const sys = d.system.find((s) => s.id === e.system);
    if (sys?.status === "planerad" && e.status !== "planerad") {
      return `en enhet i ett planerat system är också planerad: ${e.id}`;
    }
    const kn = e.kanns;
    if (kn) {
      const delar = Object.keys(kn);
      if (delar.length === 0 || delar.some((x) => !ena(x, FYNDKALLOR)))
        return `igenkänningen: ${e.id}`;
      if (kn.nmea2000) {
        const n = kn.nmea2000;
        if (Object.keys(n).length === 0) return `igenkänningen: ${e.id}`;
        const kod = n.tillverkare;
        if (kod !== undefined && !(Number.isInteger(kod) && kod >= 0 && kod <= 2047)) {
          return `tillverkarkoden: ${e.id}`;
        }
        const pgn = n.pgn;
        if (
          pgn !== undefined &&
          !(pgn.length > 0 && pgn.every((p) => Number.isInteger(p) && p > 0 && p <= 0x1ffff))
        ) {
          return `PGN:erna: ${e.id}`;
        }
        if (n.produkt !== undefined && !regex(n.produkt)) return `produktnamnet: ${e.id}`;
      }
      if (kn.masterbus && !(Number.isInteger(kn.masterbus.familj) && kn.masterbus.familj <= 255)) {
        return `familjen: ${e.id}`;
      }
      if (kn.ha) {
        // b17: en integration, flera åtskilda med |, eller * för alla.
        if (
          kn.ha.integration !== "*" &&
          !/^[a-z0-9_]{1,64}(\|[a-z0-9_]{1,64}){0,40}$/.test(kn.ha.integration)
        ) {
          return `integrationen: ${e.id}`;
        }
        if (kn.ha.modell !== undefined && !regex(kn.ha.modell)) return `modellen: ${e.id}`;
        if (kn.ha.tillverkare !== undefined && !regex(kn.ha.tillverkare))
          return `tillverkaren: ${e.id}`;
        if (kn.ha.formaga !== undefined && kn.ha.formaga !== true) return `förmågan: ${e.id}`;
        if (kn.ha.klass !== undefined && !ena(kn.ha.klass, HAKLASSER)) return `klassen: ${e.id}`;
      }
      if (kn.homey) {
        const h = kn.homey;
        for (const x of [h.klass, h.app, h.drivrutin]) {
          if (x !== undefined && !regex(x)) return `Homey: ${e.id}`;
        }
        if (h.formaga !== undefined && h.formaga !== true) return `förmågan: ${e.id}`;
      }
      if (kn.bluetooth) {
        if (!ena(kn.bluetooth.sort, BLUETOOTHSORTER)) return `sorten: ${e.id}`;
        if (kn.bluetooth.modell !== undefined && !regex(kn.bluetooth.modell))
          return `modellen: ${e.id}`;
      }
      if (kn.ecowitt && !regex(kn.ecowitt.modell)) return `modellen: ${e.id}`;
      if (kn.ecowitt?.via !== undefined && !ena(kn.ecowitt.via, ["gateway", "ha"])) {
        return `vägen för Ecowitt: ${e.id}`;
      }
      if (kn.nobo !== undefined && kn.nobo !== true) return `Nobø: ${e.id}`;
      if (kn.victron) {
        if (!ena(kn.victron.tjanst, VICTRONTJANSTER)) return `tjänsten: ${e.id}`;
        if (kn.victron.produkt !== undefined && !regex(kn.victron.produkt))
          return `produktnamnet: ${e.id}`;
      }
    }
    if (e.forbrukare) {
      const f = e.forbrukare;
      if (!ena(f.sort, ["generator", "varmare"]) || !(f.lh >= 0.1 && f.lh <= 100)) {
        return `förbrukaren: ${e.id}`;
      }
    }
    if (e.ute !== undefined && e.ute !== true) return `ute: ${e.id}`;
    // b18: larmets källa, elmätaren och väderstationen.
    if (e.larmkalla !== undefined && !ena(e.larmkalla, LARMKALLOR)) return `larmet: ${e.id}`;
    if (e.elmatare !== undefined && e.elmatare !== true) return `elmätaren: ${e.id}`;
    if (e.vaderstation !== undefined && e.vaderstation !== true) {
      return `väderstationen: ${e.id}`;
    }
    if (
      e.kopplas !== undefined &&
      (e.vaderstation !== true ||
        !Array.isArray(e.kopplas) ||
        e.kopplas.length === 0 ||
        !e.kopplas.every((f) => ena(f, FORMAGOR)))
    ) {
      return `kopplas: ${e.id}`;
    }
  }
  const signaler = new Set<string>();
  for (const s of d.signal) {
    if (!/^[a-z0-9_]+(\.[a-z0-9_]+)*$/.test(s.id) || signaler.has(s.id)) return `signalen ${s.id}`;
    signaler.add(s.id);
    if (s.signalk !== null && !/^[a-zA-Z0-9.*]{1,100}$/.test(s.signalk)) return `Signal K: ${s.id}`;
    if (typeof s.enhet !== "string" || s.enhet.length > 20 || !NAMN.test(s.beskrivning)) {
      return `signalens enhet och beskrivning: ${s.id}`;
    }
    if (s.min !== null && s.max !== null && s.min > s.max) return `signalens gränser: ${s.id}`;
  }
  // b17: förmågorna, en gång var, med signaler som finns.
  const formagor = new Set<string>();
  for (const f of d.formaga) {
    if (!ena(f.id, FORMAGOR) || formagor.has(f.id)) return `förmågan ${f.id}`;
    formagor.add(f.id);
    if (!NAMN.test(f.namn) || typeof f.perEnhet !== "boolean") return `förmågans namn: ${f.id}`;
    // b18: en förmåga utan signal (kameran och regnet) har en text, och ingen signal ute.
    if (f.signal === null) {
      if (typeof f.text !== "string" || !NAMN.test(f.text) || f.ute !== undefined) {
        return `förmågans text: ${f.id}`;
      }
      continue;
    }
    if (!signaler.has(f.signal) || (f.ute !== undefined && !signaler.has(f.ute))) {
      return `förmågans signal: ${f.id}`;
    }
  }
  const kopplingar = new Set<string>();
  for (const x of d.koppling) {
    if (!enheter.has(x.enhet) || !signaler.has(x.signal))
      return `kopplingen ${x.enhet}/${x.signal}`;
    const n = `${x.enhet}/${x.signal}`;
    if (kopplingar.has(n)) return `kopplingen ${n} två gånger`;
    kopplingar.add(n);
  }
  const mallar = new Set<string>();
  for (const m of d.mall) {
    if (!ID.test(m.id) || mallar.has(m.id)) return `mallen ${m.id}`;
    mallar.add(m.id);
    if (!Array.isArray(m.platstyper) || m.platstyper.length === 0)
      return `mallens platstyper: ${m.id}`;
    if (m.platstyper.some((t) => !ena(t, PLATSTYPER))) return `mallens platstyper: ${m.id}`;
    if (!produkttext(m.namn) || !produkttext(m.text)) return `mallens text: ${m.id}`;
    if (m.granser.some((g) => !/^[a-z0-9_.]+$/.test(g.grans) || !Number.isFinite(g.varde))) {
      return `mallens gränser: ${m.id}`;
    }
    if (m.bat !== undefined && !(m.platstyper.length === 1 && m.platstyper[0] === "bat")) {
      return `båtens uppställning finns bara i en mall för båtar: ${m.id}`;
    }
    if (m.bat) {
      const platser = new Set<string>();
      for (const x of m.bat.motorer) {
        if (!/^(babord|styrbord|mitten|generator)[2-4]?$/.test(x.plats) || platser.has(x.plats)) {
          return `mallens motor: ${m.id}`;
        }
        platser.add(x.plats);
        if ((x.sort === "generator") !== x.plats.startsWith("generator")) {
          return `generatorns plats: ${m.id}`;
        }
        if (x.tank !== null && (x.tank < 0 || x.tank >= Math.max(m.bat.tankar.length, 1))) {
          return `mallens matning: ${m.id}`;
        }
      }
    }
  }
  const nr = new Set<number>();
  for (const f of d.fraga) {
    if (!ID.test(f.id) || !Number.isInteger(f.nr) || f.nr < 1 || f.nr > 8 || nr.has(f.nr)) {
      return `frågan ${f.id}`;
    }
    nr.add(f.nr);
    if (!produkttext(f.text) || (f.hjalp !== null && !produkttext(f.hjalp))) {
      return `frågans text: ${f.id}`;
    }
    if (f.alternativ.some((a) => !ID.test(a.id) || !produkttext(a.text))) {
      return `frågans alternativ: ${f.id}`;
    }
  }
  if (d.fraga.length > 8) return "högst åtta frågor";
  const n = d.nmea2000;
  if (
    !n ||
    !Array.isArray(n.tillverkare) ||
    !Array.isArray(n.klasser) ||
    !Array.isArray(n.funktioner)
  ) {
    return "uppslagen för NMEA 2000";
  }
  return null;
}
